import { useMsal, useIsAuthenticated } from '@azure/msal-react';
import { InteractionRequiredAuthError } from '@azure/msal-browser';
import { useCallback, useMemo } from 'react';
import { apiTokenRequest } from './msalConfig';

export function useAuth() {
  const { instance, accounts } = useMsal();
  const isAuthenticated = useIsAuthenticated();
  const account = accounts[0] ?? null;

  const login = useCallback(() => {
    instance.loginRedirect({ scopes: ['openid', 'profile'] });
  }, [instance]);

  const logout = useCallback(() => {
    instance.logoutRedirect({ postLogoutRedirectUri: '/' });
  }, [instance]);

  const getAccessToken = useCallback(async (): Promise<string> => {
    if (!account) throw new Error('No authenticated account');
    try {
      const response = await instance.acquireTokenSilent({
        ...apiTokenRequest,
        account,
      });
      return response.accessToken;
    } catch (error) {
      if (error instanceof InteractionRequiredAuthError) {
        await instance.acquireTokenRedirect(apiTokenRequest);
        return '';
      }
      throw error;
    }
  }, [instance, account]);

  const roles = useMemo<string[]>(() => {
    if (!account?.idTokenClaims) return [];
    const claims = account.idTokenClaims as Record<string, unknown>;
    const r = claims['roles'];
    return Array.isArray(r) ? r : [];
  }, [account]);

  const displayName = account?.name ?? account?.username ?? '';
  const email = account?.username ?? '';
  const entraId = account?.localAccountId ?? '';

  return {
    isAuthenticated,
    account,
    displayName,
    email,
    entraId,
    roles,
    login,
    logout,
    getAccessToken,
  };
}
